import React, { createContext, useContext, useRef } from 'react';
import classNames from 'classnames';
import { useRadio, useRadioGroup } from '@react-aria/radio';
import { useFocusRing } from '@react-aria/focus';
import { useVisuallyHidden } from '@react-aria/visually-hidden';
import { useRadioGroupState } from 'react-stately';
import { StyledRadioGroup } from './RadioGroup.styles';
export const RadioContext = createContext(null);
/**
 * Renders a group of radio buttons with no styling. This component is not
 * designed to be user-facing, and should only be used to provide functionality.
 *
 * A group label must be included: either pass a string or markup into the
 * `label` prop, or include an `aria-label` or `aria-labelledby` attribute.
 */
export function RadioGroup(props) {
    const { children, className, value, id, isDisabled, isReadOnly, label, name, onChange, orientation, } = props;
    const radioGroupStateProps = {
        isDisabled: isDisabled || false,
        isReadOnly: isReadOnly || false,
        name: name || '',
        value,
        onChange,
        children,
    };
    const state = useRadioGroupState(radioGroupStateProps);
    const { radioGroupProps, labelProps } = useRadioGroup(props, state);
    return (React.createElement(StyledRadioGroup, Object.assign({}, radioGroupProps, { className: classNames(className, { horizontal: orientation === 'horizontal' }), "data-cy": props['data-cy'], id: id }),
        label && (React.createElement("div", Object.assign({ className: "groupLabel" }, labelProps), label)),
        React.createElement(RadioContext.Provider, { value: state },
            React.createElement(React.Fragment, null, children))));
}
/**
 * A single radio button and its label; no styling is applied, and the native
 * radio button is visually hidden.
 */
export function Radio(props) {
    const { children, className, isDisabled, isVisuallySelected } = props;
    const state = useContext(RadioContext);
    const inputRef = useRef(null);
    const { visuallyHiddenProps } = useVisuallyHidden();
    const { inputProps } = useRadio(props, state, inputRef);
    const { isFocusVisible, focusProps } = useFocusRing();
    let isSelected;
    if (isVisuallySelected) {
        isSelected = isVisuallySelected(state === null || state === void 0 ? void 0 : state.selectedValue);
    }
    else {
        isSelected = (state === null || state === void 0 ? void 0 : state.selectedValue) === props.value;
    }
    return (React.createElement("label", { className: classNames(className, {
            disabled: isDisabled || (state === null || state === void 0 ? void 0 : state.isDisabled),
            selected: isSelected,
            focused: isFocusVisible,
        }) },
        React.createElement("input", Object.assign({}, inputProps, visuallyHiddenProps, focusProps, { ref: inputRef })),
        children));
}
//# sourceMappingURL=RadioGroup.js.map